import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

const CHAPTERS = [
    {
        index: "01",
        title: "Scale",
        copy: "A fleet built for every tonnage — from a single 20 ft box to multi-axle loads of industrial plant. If it fits on a trailer, we move it.",
    },
    {
        index: "02",
        title: "Precision",
        copy: "Route surveys, permits, escorts and loading supervision handled in-house. Your cargo moves on a plan, not on a promise.",
    },
    {
        index: "03",
        title: "Trust",
        copy: "Nineteen years on Indian highways. Three companies, one family, and a single phone call between you and your delivery.",
    },
];

const STATS = [
    { value: 19, suffix: "+", label: "Years on the road" },
    { value: 3, suffix: "", label: "Sister companies" },
    { value: 28, suffix: "", label: "States covered" },
    { value: 40, suffix: "K+", label: "Trips completed" },
];

function Stat({ value, suffix, label, testid }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, value, {
            duration: 1.8,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setDisplay(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, value]);

    return (
        <div ref={ref} className="p-8 md:p-12" data-testid={testid}>
            <p className="font-display text-6xl text-white md:text-7xl">
                {display}
                <span className="text-blaze">{suffix}</span>
            </p>
            <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-white/45 md:text-xs">{label}</p>
        </div>
    );
}

export default function Manifesto() {
    return (
        <section id="why-us" className="border-t border-white/10 px-5 py-24 md:px-10 md:py-36" data-testid="manifesto-section">
            <div className="grid gap-16 lg:grid-cols-12 lg:gap-10">
                <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-32">
                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7 }}
                            className="mb-4 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-blaze md:text-xs"
                        >
                            <span className="inline-block h-2 w-2 bg-blaze" />
                            The Akash Way
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="font-display text-4xl uppercase leading-[0.95] tracking-tight md:text-6xl"
                        >
                            Why India ships <span className="text-outline-faint">with us.</span>
                        </motion.h2>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    {CHAPTERS.map((chapter, i) => (
                        <motion.div
                            key={chapter.index}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                            className="grid grid-cols-[auto_1fr] gap-6 border-b border-white/10 py-10 first:border-t md:gap-12 md:py-14"
                            data-testid={`manifesto-chapter-${chapter.index}`}
                        >
                            <span className="font-display text-5xl text-blaze/90 md:text-7xl">{chapter.index}</span>
                            <div>
                                <h3 className="mb-3 font-display text-2xl uppercase tracking-wide md:text-4xl">{chapter.title}</h3>
                                <p className="max-w-lg text-sm leading-relaxed text-white/55 md:text-base">{chapter.copy}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="mt-20 grid grid-cols-2 border border-white/10 md:mt-28 lg:grid-cols-4"
                data-testid="stats-grid"
            >
                {STATS.map((stat, i) => (
                    <div key={stat.label} className="[&:not(:last-child)]:border-white/10 [&:nth-child(-n+2)]:border-b lg:[&:nth-child(-n+2)]:border-b-0 lg:[&:not(:last-child)]:border-r [&:nth-child(odd)]:border-r lg:[&:nth-child(odd)]:border-r">
                        <Stat {...stat} testid={`stat-${i + 1}`} />
                    </div>
                ))}
            </motion.div>
        </section>
    );
}
